import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, TimeoutError } from 'rxjs';
import { authRoutesNames } from 'src/core/routes/auth/auth.routes.names';
import { coreRoutesNames } from 'src/core/routes/core.routes.names';
import { AuthStore } from 'src/core/stores';

type Error = {
  generic: boolean;
  invalidCredentials: boolean;
  timeout: boolean;
};

@Component({
  selector: 'core-login',
  templateUrl: './login.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    TranslocoDirective,
  ],
})
export class LoginComponent {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  readonly authStore = inject(AuthStore);
  readonly translocoService = inject(TranslocoService);
  private formBuilder = inject(FormBuilder);

  readonly showPassword = signal(false);

  readonly getLoginFormGroupPasswordInputType = computed(() =>
    this.showPassword() ? 'text' : 'password',
  );

  private error$$ = new BehaviorSubject<Error>({
    generic: false,
    invalidCredentials: false,
    timeout: false,
  });
  error$ = this.error$$.asObservable();

  readonly loginFormGroup = this.initLoginFormGroup();

  redirectUrl: string =
    this.activatedRoute.snapshot.queryParams['redirect'] || '/';

  coreRoutesNames = coreRoutesNames;
  authRoutesNames = authRoutesNames;

  constructor() {
    effect(() => {
      if (this.authStore.isAuthenticated()) {
        this.router.navigateByUrl(this.redirectUrl);
      }
    });
  }

  get loginFormGroupEmail() {
    return this.loginFormGroup.get('email');
  }

  get loginFormGroupPassword() {
    return this.loginFormGroup.get('password');
  }

  login() {
    if (this.loginFormGroup.invalid) {
      return;
    }

    this.error$$.next({
      generic: false,
      invalidCredentials: false,
      timeout: false,
    });

    this.authStore
      .login(
        this.loginFormGroupEmail?.value || '',
        this.loginFormGroupPassword?.value || '',
      )
      .subscribe({
        next: () => {
          this.router.navigateByUrl(this.redirectUrl);
        },
        error: (error: unknown) => {
          if (error instanceof TimeoutError) {
            this.error$$.next({
              generic: false,
              invalidCredentials: false,
              timeout: true,
            });
          } else if (
            error instanceof HttpErrorResponse &&
            error.status === 401
          ) {
            this.error$$.next({
              generic: false,
              invalidCredentials: true,
              timeout: false,
            });
          } else {
            this.error$$.next({
              generic: true,
              invalidCredentials: false,
              timeout: false,
            });
          }
        },
      });
  }

  register() {
    this.router.navigate([coreRoutesNames.AUTH, authRoutesNames.REGISTER]);
  }

  toggleShowPassword(): void {
    this.showPassword.update((showPassword) => !showPassword);
  }

  private initLoginFormGroup() {
    return this.formBuilder.group({
      email: new FormControl(this.authStore.currentUser()?.email, {
        validators: [Validators.required, Validators.email],
      }),
      password: new FormControl('', {
        validators: [Validators.required],
      }),
    });
  }
}
